/**
 * Checkbox Component
 *
 * Styled checkbox with label support.
 */

import React from 'react';

interface CheckboxProps {
  id?: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  count?: number;
  disabled?: boolean;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  id,
  label,
  checked,
  onChange,
  count,
  disabled = false,
}) => {
  const inputId = id || `checkbox-${label.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <label
      htmlFor={inputId}
      className={`flex items-center gap-3 py-1.5 cursor-pointer ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:text-primary-600'
      }`}
    >
      <div className="relative">
        <input
          id={inputId}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          className="peer sr-only"
        />
        <div className="w-5 h-5 border-2 border-gray-300 rounded transition-colors peer-checked:border-primary-500 peer-checked:bg-primary-500 peer-focus:ring-2 peer-focus:ring-primary-200">
          {checked && (
            <svg
              className="w-full h-full text-white p-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>
      </div>
      <span className="flex-1 text-sm text-gray-700">{label}</span>
      {count !== undefined && (
        <span className="text-xs text-gray-400">({count})</span>
      )}
    </label>
  );
};

interface CheckboxGroupProps {
  options: Array<{ value: string; label: string; count?: number }>;
  value: string[];
  onChange: (value: string[]) => void;
  maxVisible?: number;
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  options,
  value,
  onChange,
  maxVisible,
}) => {
  const [showAll, setShowAll] = React.useState(false);
  const visibleOptions =
    maxVisible && !showAll ? options.slice(0, maxVisible) : options;
  const hasMore = maxVisible && options.length > maxVisible;

  const handleChange = (optionValue: string, checked: boolean) => {
    if (checked) {
      onChange([...value, optionValue]);
    } else {
      onChange(value.filter((v) => v !== optionValue));
    }
  };

  return (
    <div className="space-y-1">
      {visibleOptions.map((option) => (
        <Checkbox
          key={option.value}
          label={option.label}
          checked={value.includes(option.value)}
          onChange={(checked) => handleChange(option.value, checked)}
          count={option.count}
        />
      ))}
      {hasMore && (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className="text-sm text-primary-600 font-medium mt-2 hover:text-primary-700"
        >
          {showAll ? 'Show less' : `Show ${options.length - maxVisible!} more`}
        </button>
      )}
    </div>
  );
};

export default Checkbox;
